import React, {
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Controller,
  FieldValues,
  Path,
  useForm,
  UseFormRegister,
  Validate,
} from 'react-hook-form'
import { Menu } from '@headlessui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import moment, { Moment } from 'moment'
import useProfile from '../../../../common/providers/useProfile'
import { CheckboxGroup } from '../../common/components/Inputs/CheckboxGroup'
import { Error } from '../../icons'
import useInviteLink from '../../../../common/providers/useInviteLink'
import useIndustries from '../../../../common/providers/useIndustries'
import useOccupation from '../../../../common/providers/useOccupation'
import { Avatars } from '../../assets/avatars'
import useUploadUserAvatar from '../../common/providers/useUploadUserAvatar'
import {
  HeaderBackgroundDesktop,
  HeaderBackgroundMobile,
} from './HeaderBackground'

interface InputFieldProps<T extends FieldValues>
  extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  className?: string
  errors?: any
  setError?: any
  errorMessage?: string
  register?: UseFormRegister<T>
  regexPattern?: RegExp
  required?: boolean
  validate?: () =>
    | Validate<any, FieldValues>
    | Record<string, Validate<any, FieldValues>>
  password?: string
  repeatPassword?: string
  biggerInput?: boolean
  readOnly?: boolean
  onClick?: () => void
  icon?: ReactNode
  labelSize?: string
  innerClassName?: string
  id: Path<T>
}

const InputField = ({
  register,
  required,
  validate,
  regexPattern,
  label,
  className,
  errors,
  setError,
  errorMessage,
  innerClassName,
  labelSize,
  ...props
}: InputFieldProps<{
  first_name: string
  last_name: string
  email: string
  phone_number: string
  date_of_birth: string
  occupation: string
  postal_code: string
  county: string
  town: string
  address_line_1: string
  gender: 'MALE' | 'FEMALE' | 'OTHER' | 'PREFER_NOT_TO_SAY'
  tradingAddress: string
  tradingCity: string
  tradingCounty: string
  tradingPostcode: string
  avatar: string
  industries: string
  businessType: string
  businessName: string
}>) => (
  <div
    className={`w-full flex flex-col justify-self-start item-center ${
      props.readOnly && 'cursor-pointer'
    }`}
  >
    <div
      className={`relative ${className}`}
      onClick={props.onClick && props.onClick}
    >
      {props.icon && (
        <div className='absolute inset-y-0 right-4 flex items-center'>
          {props.icon}
        </div>
      )}
      <input
        {...register(props.id, {
          required: required,
          pattern: regexPattern,
          // @ts-ignore
          validate: (() => {
            if (props.password !== props.repeatPassword) {
              setError(props.id, {
                type: 'manual',
                message: "The password doesn't match",
              })
            }
          })(),
        })}
        type='text'
        onChange={(e) => register(props.id).onChange(e)}
        onBlur={(e) => register(props.id).onBlur(e)}
        className={`${innerClassName} ${
          props.disabled && '!bg-[#F4F4F4] !text-[#A0ABBE]'
        } h-full peer block shadow-[0px_0.8px_2px_0px_rgba(0,0,0,0.15)_inset] w-full appearance-none rounded-lg bg-white p-[4px_10px] pt-[16px] text-gray-900 placeholder-transparent !text-[14px] focus:outline-none ${
          errors?.[props.id]
            ? 'border-[#DE3333]'
            : 'border-gray-300 focus:border-teal-500'
        } ${props.readOnly && 'cursor-pointer'}`}
        aria-labelledby={`${props.id}-label`}
        aria-invalid={errors?.[props.id] ? 'true' : 'false'}
        readOnly={props.readOnly}
        {...props}
      />
      <label
        htmlFor={props.id}
        id={`${props.id}-label`}
        className={`text-xs md:text-[12px] absolute left-[10px] max-w-[calc(100%-20px)] whitespace-nowrap overflow-hidden text-ellipsis top-[13px] transform -translate-y-1/2 transition-all duration-200 ease-in-out
            peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:text-[#818285] ${
              props.disabled && '!text-[#A0ABBE]'
            }
            peer-focus:top-[10px] peer-focus:text-[12px] ${
              errors?.[props.id]
                ? '!peer-focus:text-[#DE3333] text-[16px] !peer-placeholder-shown:text-[#DE3333] !text-[#DE3333]'
                : 'peer-focus:text-teal-600'
            }`}
      >
        {!props.readOnly && label}
      </label>
    </div>
    {errors?.[props.id]?.type && (
      <div className='flex justify-self-start items-center pt-1 gap-y-4'>
        <Error width='18px' height='18px' />
        <span
          className={`${
            labelSize ? `text-[${labelSize}]` : 'text-sm'
          } text-[#DE3333] font-normal`}
        >
          {errorMessage}
        </span>
      </div>
    )}
  </div>
)

const DropdownMenu = ({ id, label, options, selected, setValue }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [labelText, setLabelText] = useState<string>(label)
  const menuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [menuRef])

  const dropdownLabels = ['Occupation', 'Industry', 'Business category']

  return (
    <Menu
      as='div'
      className='relative flex justify-between items-center w-full'
      id={'dropdown' + id}
    >
      {({ open }) => (
        <>
          <Menu.Button
            className='w-full flex whitespace-nowrap justify-between items-center rounded-md bg-white py-2 text-sm shadow-[0px_0.8px_2px_0px_rgba(0,0,0,0.15)_inset] ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className='truncate ... w-[90%] text-left ml-3 text-[#818285] text-sm md:text-base'>
              {dropdownLabels.includes(labelText) && selected
                ? selected
                : labelText}
            </span>
            {open ? (
              <ChevronUpIcon
                aria-hidden='true'
                className='mr-2 size-5 text-[#37A2BA]'
              />
            ) : (
              <ChevronDownIcon
                aria-hidden='true'
                className='mr-2 size-5 text-[#37A2BA]'
              />
            )}
          </Menu.Button>
          <Menu.Items
            ref={menuRef}
            className='absolute left-0 top-10 z-10 mt-2 overflow-y-auto max-h-[200px] px-[8px] py-[4px] w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in'
          >
            <div className='py-1'>
              {options?.map((option) => (
                <Menu.Item key={option.id}>
                  {() => (
                    <option
                      onClick={(event) => {
                        setValue(id, option.id)
                        setLabelText(event.currentTarget.textContent)
                        setIsOpen(false)
                      }}
                      className='cursor-pointer hover:bg-gray-100 block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none'
                    >
                      {option.name}
                    </option>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </>
      )}
    </Menu>
  )
}

const StyledDatePicker = ({ control }) => (
  <Controller
    name='date_of_birth'
    control={control}
    rules={{ required: 'Date of birth is required' }}
    render={({ field }) => (
      <DatePicker
        label='Date of Birth'
        value={field.value ? moment(field.value) : moment(new Date())}
        onChange={(newValue: Moment | null) => {
          field.onChange(
            newValue ? newValue.toDate().toISOString().split('T')[0] : '',
          ) // Update react-hook-form state
        }}
        format='D MMM YYYY'
        sx={{
          '& .MuiInputBase-input': {
            padding: '8px 14px',
          },
        }}
      />
    )}
  />
)

const BUSINESS_TYPES = [
  { id: 0, name: 'LIMITED_COMPANY' },
  { id: 1, name: 'LIMITED_LIABILITY_PARTNERSHIP' },
  { id: 2, name: 'PARTNERSHIP' },
  { id: 3, name: 'SOLE_TRADER' },
  { id: 4, name: 'CHARITY' },
  { id: 5, name: 'OTHER' },
]

const Profile = () => {
  const { profile, updateProfile, getProfile } = useProfile()
  const { acceptedLink } = useInviteLink()
  const { getIndustries, industries } = useIndustries()
  const { createUploadUserAvatar } = useUploadUserAvatar()
  const { getOccupations, occupations } = useOccupation()
  const [profileUpdated, setProfileUpdated] = useState<boolean>(false)
  const [sameAddress, setSameAddress] = useState<boolean>(false)
  const [isChecked, setIsChecked] = useState<{
    male: boolean
    female: boolean
    other: boolean
    preferNotToSay: boolean
  }>({
    male: profile.gender === 'MALE',
    female: profile.gender === 'FEMALE',
    other: profile.gender === 'OTHER',
    preferNotToSay: profile.gender === 'PREFER_NOT_TO_SAY',
  })

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    setError,
    control,
    setValue,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      email: profile.email,
      phone_number: profile.phone_number || '',
      date_of_birth: profile.date_of_birth || moment(new Date()).toISOString(),
      occupation:
        occupations?.find(
          (occupation) => occupation.name === profile.occupation,
        )?.name || '',
      postal_code: profile.postal_code || '',
      county: profile.county || '',
      town: profile.town || '',
      address_line_1: profile.address_line_1 || '',
      gender: profile.gender || null,
      tradingAddress: profile.business_profile?.address_line_1 || '',
      tradingCity: profile.business_profile?.town || '',
      tradingCounty: profile.business_profile?.county || '',
      tradingPostcode: profile.business_profile?.postal_code || '',
      avatar: profile.avatar || null,
      industries:
        industries?.find(
          (industry) => industry.id === profile.business_profile?.industries[0],
        )?.id || '',
      businessType: profile.business_profile?.business_type || '',
      businessName: profile.business_profile?.business_name || '',
    },
  })

  const hasAnyFieldBeenTouched =
    Object.keys(touchedFields).length > 0 ||
    Object.values(isChecked).some((value) => value === true)

  useEffect(() => {
    if (hasAnyFieldBeenTouched) {
      setProfileUpdated(true)
    }
  }, [hasAnyFieldBeenTouched])

  useEffect(() => {
    getProfile()
    getIndustries({})
    getOccupations({})
  }, [getProfile, getIndustries, getOccupations])

  const onSubmit = async (values) => {
    createUploadUserAvatar(
      { file: avatarFile },
      {
        // the business does not require UI feedback (?!) so this is just for devs to know it works!
        onSuccess: () => {
          console.log('Your avatar has been updated')
        },
      },
    )

    const {
      tradingAddress,
      tradingCity,
      tradingCounty,
      tradingPostcode,
      avatar,
      industries,
      businessType,
      businessName,
      ...rest
    } = values
    const isBusinessProfile =
      profile.account_type === 'BUSINESS' ||
      tradingCity ||
      tradingCounty ||
      tradingPostcode ||
      tradingAddress ||
      industries ||
      businessType ||
      businessName
    updateProfile(
      {
        ...rest,
        account_type: isBusinessProfile ? 'BUSINESS' : 'CONSUMER',
        business_profile: isBusinessProfile
          ? {
              address_line_1: tradingAddress,
              town: tradingCity,
              county: tradingCounty,
              postal_code: tradingPostcode,
              industries: industries ? [industries] : [],
              business_name: businessName,
              business_type: BUSINESS_TYPES[businessType]?.name,
            }
          : null,
        used_invite_token: acceptedLink,
      },
      {
        onSuccess: () => {
          // We don't show any UI confirmation (business didn't want it), so we just log this for dev purposes
          console.info('Your profile has been updated')
        },
      },
    )
  }

  // const [companyList, setCompanyList] = useState<string[]>([])

  // useEffect(() => {
  //   fetch(
  //     'https://api.company-information.service.gov.uk/advanced-search/companies',
  //     {
  //       method: 'GET',
  //       headers: {
  //         'Authorization': `Basic ${btoa(
  //           '8c69ad3f-1743-42e5-a5e7-91037df7ca99' + ':',
  //         )}`, // Base64 encoding
  //       },
  //     },
  //   )
  //     .then((response) => response.json())
  //     .then((data) => {
  //       setCompanyList(data)
  //     })
  // }, [])

  useEffect(() => {
    if (isChecked.male) {
      setValue('gender', 'MALE')
    } else if (isChecked.female) {
      setValue('gender', 'FEMALE')
    } else if (isChecked.other) {
      setValue('gender', 'OTHER')
    } else {
      setValue('gender', 'PREFER_NOT_TO_SAY')
    }
  }, [isChecked, profile.gender])

  const [avatarFile, setAvatarFile] = useState<File>()

  return (
    <div className='min-h-screen w-full flex flex-col justify-start items-center'>
      <div className='relative w-full'>
        <div className='hidden md:flex'>
          <HeaderBackgroundDesktop />
        </div>
        <div className='flex md:hidden'>
          <HeaderBackgroundMobile />
        </div>
        <div className='flex flex-col gap-2 absolute left-[33px] md:left-[115px] top-[44px] md:top-[103px]'>
          <img
            className='w-[60px] h-[60px] md:w-[80px] md:h-[80px] rounded-full'
            src={!profile.avatar ? Avatars._Dummy : profile.avatar}
            alt='placehodler avatar'
          />
          <div className='flex flex-col md:flex-row justify-center md:justify-between items-start md:items-center '>
            <div className='flex flex-col justify-start md:justify-center items-center '>
              <h1 className='text-[#001A3D] text-lg font-bold leading-normal mt-8'>
                {profile.first_name} {profile.last_name}
              </h1>
              <span className='text-[#001A3D] text-sm font-normal leading-normal'>
                {profile.email}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className='flex flex-col items-center gap-[25px] md:gap-[60px] pt-[126px] md:pt-[18px] pr-[12px] md:pr-[32px] w-full'>
        <div className='flex flex-col w-full items-end'>
          <label
            htmlFor='avatar'
            className='self-end text-[#001A3D] text-center text-sm font-medium leading-normal underline hover:text-[#6596D7] cursor-pointer'
          >
            Upload your photo
          </label>
          <input
            id='avatar'
            type='file'
            className='hidden'
            onChange={(e) => {
              setAvatarFile(e.target.files[0])
            }}
          />
        </div>
        <form
          encType='multipart/form-data'
          className='h-full max-w-[91rem] w-full px-6 md:px-10 flex flex-col items-center space-y-10'
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className='flex flex-col md:flex-row md:justify-between items-start gap-10 md:gap-14 md:max-w-[1380px] w-full mx-auto'>
            <div className='rounded-[12px] border-[0.6px] border-[#D1D3D7] bg-white shadow-[0.2px_0.2px_70px_0px_rgba(110,119,133,0.04),-0.2px_-0.2px_70px_0px_rgba(110,119,133,0.04)] w-full md:w-[65%]'>
              <div className='p-[40px] h-[100px] md:h-[171px] rounded-tl-[12px] rounded-tr-[12px] rounded-bl-[40px] rounded-br-[40px] text-white bg-[#101828] flex justify-center items-center'>
                <span className='text-xl lg:text-2xl font-medium leading-[24px] tracking-[0.4px] text-[#CCE0DD]'>
                  Personal info
                </span>
              </div>
              <div className='px-[30px] py-[40px]'>
                <div className='grid md:grid-cols-4 gap-4'>
                  <div className='flex flex-col col-span-2 item-center'>
                    <InputField
                      setError={setError}
                      register={register}
                      required={true}
                      id='first_name'
                      label='* First name'
                      placeholder='first_name'
                      className=''
                      errors={errors}
                      errorMessage='Please enter your first name'
                    />
                  </div>
                  <div className='flex flex-col col-span-2 item-center'>
                    <InputField
                      setError={setError}
                      register={register}
                      required={true}
                      id='last_name'
                      label='* Last name'
                      placeholder='last_name'
                      className=''
                      errors={errors}
                      errorMessage='Please enter your last name'
                    />
                  </div>
                  <div className='flex flex-col col-span-2 item-center'>
                    <InputField
                      setError={setError}
                      register={register}
                      required={true}
                      id='email'
                      label='* Email address'
                      placeholder='email'
                      className=''
                      errors={errors}
                      errorMessage='Please enter your email address'
                    />
                  </div>
                  <div className='flex flex-col col-span-2 item-center'>
                    <InputField
                      setError={setError}
                      register={register}
                      required={true}
                      id='phone_number'
                      label='* Phone number'
                      placeholder='phone_number'
                      className=''
                      errors={errors}
                      errorMessage='Please enter your phone number'
                    />
                  </div>
                  <div className='flex flex-col col-span-2 item-center'>
                    <StyledDatePicker control={control} />
                  </div>
                  <div className='flex flex-col col-span-2 item-center'>
                    <DropdownMenu
                      id='occupation'
                      label='Occupation'
                      options={occupations}
                      selected={
                        occupations?.find(
                          (occupation) =>
                            occupation.name === profile.occupation,
                        )?.name
                      }
                      setValue={setValue}
                    />
                  </div>
                </div>
                <h5 className='text-[#101828] text-[16px] font-medium leading-normal mb-2 mt-4'>
                  Home address
                </h5>
                <div className='grid md:grid-cols-4 gap-4'>
                  <div className='flex flex-col col-span-2 item-center'>
                    <InputField
                      setError={setError}
                      register={register}
                      required={true}
                      id='address_line_1'
                      label='* Address Line 1'
                      placeholder='address'
                      className=''
                      errors={errors}
                      errorMessage='Please enter your address'
                    />
                  </div>
                  <div className='flex flex-col col-span-2 item-center'>
                    <InputField
                      setError={setError}
                      register={register}
                      required={true}
                      id='town'
                      label='* City'
                      placeholder='city'
                      className=''
                      errors={errors}
                      errorMessage='Please enter your city'
                    />
                  </div>
                  <div className='flex flex-col col-span-2 item-center'>
                    <InputField
                      setError={setError}
                      register={register}
                      required={true}
                      id='county'
                      label='* County'
                      placeholder='county'
                      className=''
                      errors={errors}
                      errorMessage='Please enter your county'
                    />
                  </div>
                  <div className='flex flex-col col-span-2 item-center'>
                    <InputField
                      setError={setError}
                      register={register}
                      required={true}
                      id='postal_code'
                      label='* Postcode'
                      placeholder='postcode'
                      className=''
                      errors={errors}
                      errorMessage='Please enter your postcode'
                    />
                  </div>
                  <div className='mb-8 col-span-3 w-full flex flex-col justify-between items-start'>
                    <h5 className='text-[#101828] text-[16px] font-medium leading-normal mb-2 mt-4'>
                      Gender
                    </h5>
                    <CheckboxGroup
                      className='mt-2'
                      id='male'
                      label={
                        <span className='text-right text-[#6E7785] text-sm'>
                          Male
                        </span>
                      }
                      name='male'
                      checked={isChecked.male}
                      onChange={() =>
                        setIsChecked({
                          male: !isChecked.male,
                          female: false,
                          other: false,
                          preferNotToSay: false,
                        })
                      }
                    />
                    <CheckboxGroup
                      className='mt-2'
                      id='female'
                      label={
                        <span className='text-right text-[#6E7785] text-sm'>
                          Female
                        </span>
                      }
                      name='female'
                      checked={isChecked.female}
                      onChange={() =>
                        setIsChecked({
                          male: false,
                          female: !isChecked.female,
                          other: false,
                          preferNotToSay: false,
                        })
                      }
                    />
                    <CheckboxGroup
                      className='mt-2'
                      id='other'
                      label={
                        <span className='text-right text-[#6E7785] text-sm'>
                          Other
                        </span>
                      }
                      name='other'
                      checked={isChecked.other}
                      onChange={() =>
                        setIsChecked({
                          male: false,
                          female: false,
                          other: !isChecked.other,
                          preferNotToSay: false,
                        })
                      }
                    />
                    <CheckboxGroup
                      className='mt-2'
                      id='preferNotToSay'
                      label={
                        <span className='text-right text-[#6E7785] text-sm'>
                          Prefer not to say
                        </span>
                      }
                      name='preferNotToSay'
                      checked={isChecked.preferNotToSay}
                      onChange={() =>
                        setIsChecked({
                          male: false,
                          female: false,
                          other: false,
                          preferNotToSay: !isChecked.preferNotToSay,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='rounded-[12px] border-[0.6px] border-[#D1D3D7] bg-white shadow-[0.2px_0.2px_70px_0px_rgba(110,119,133,0.04),-0.2px_-0.2px_70px_0px_rgba(110,119,133,0.04)] w-full md:w-[35%]'>
              <div className='p-[40px] h-[100px] md:h-[171px] rounded-tl-[12px] rounded-tr-[12px] rounded-bl-[40px] rounded-br-[40px] text-white bg-[#101828] flex justify-center items-center'>
                <span className='text-xl lg:text-2xl font-medium leading-[24px] tracking-[0.4px] text-[#CCE0DD]'>
                  Professional info
                </span>
              </div>
              <div className='flex flex-col justify-center items-center gap-4 px-[30px] py-[40px]'>
                {/*<Combobox*/}
                {/*  id='country'*/}
                {/*  label='Company'*/}
                {/*  items={[*/}
                {/*    'United States',*/}
                {/*    'Canada',*/}
                {/*    'United Kingdom',*/}
                {/*    'Australia',*/}
                {/*  ]}*/}
                {/*  register={register}*/}
                {/*  required={true}*/}
                {/*  errors={errors}*/}
                {/*  errorMessage='Please select a country'*/}
                {/*  onSelect={(value) => console.log('Selected:', value)}*/}
                {/*/>*/}
                <div className='w-full'>
                  <InputField
                    setError={setError}
                    register={register}
                    id='businessName'
                    label='Business name'
                    placeholder='businessName'
                    className='w-full'
                    errors={errors}
                    errorMessage='Please enter your business name'
                  />
                </div>
                <DropdownMenu
                  id='industries'
                  label='Industry'
                  options={industries}
                  selected={
                    industries?.find(
                      (industry) =>
                        industry.id === profile.business_profile?.industries[0],
                    )?.name
                  }
                  setValue={setValue}
                />
                <DropdownMenu
                  id='businessType'
                  label='Business category'
                  options={BUSINESS_TYPES}
                  selected={
                    BUSINESS_TYPES?.find(
                      (business_type) =>
                        business_type.name === business_type.name,
                    )?.name
                  }
                  setValue={setValue}
                />
                <div className='mb-2 w-full flex flex-col justify-between items-start'>
                  <h5 className='text-[#101828] text-[16px] font-medium leading-normal mb-2 mt-3'>
                    Registered trading address
                  </h5>
                  <CheckboxGroup
                    id='sameAddress'
                    label={
                      <span className='text-right text-[#6E7785] text-sm'>
                        Use the same as personal address
                      </span>
                    }
                    name='sameAddress'
                    checked={sameAddress}
                    onChange={() => {
                      setSameAddress(!sameAddress)
                      if (!sameAddress) {
                        setValue('tradingAddress', profile.address_line_1)
                        setValue('tradingCity', profile.town)
                        setValue('tradingCounty', profile.county)
                        setValue('tradingPostcode', profile.postal_code)
                      } else {
                        setValue('tradingAddress', '')
                        setValue('tradingCity', '')
                        setValue('tradingCounty', '')
                        setValue('tradingPostcode', '')
                      }
                    }}
                  />
                </div>
                <div className='w-full'>
                  <InputField
                    register={register}
                    id='tradingAddress'
                    label='Registered trading address line 1'
                    placeholder='tradingAddress'
                    className='w-full'
                    errors={errors}
                    errorMessage='Please enter your registered trading address'
                  />
                </div>
                <div className='w-full'>
                  <InputField
                    register={register}
                    id='tradingCity'
                    label='City'
                    placeholder='tradingCity'
                    className='w-full'
                    errors={errors}
                    errorMessage='Please enter your city'
                  />
                </div>
                <div className='w-full'>
                  <InputField
                    register={register}
                    id='tradingCounty'
                    label='County'
                    placeholder='tradingCounty'
                    className='w-full'
                    errors={errors}
                    errorMessage='Please enter your county'
                  />
                </div>
                <div className='w-full'>
                  <InputField
                    register={register}
                    id='tradingPostcode'
                    label='Postcode'
                    placeholder='tradingPostcode'
                    className='w-full'
                    errors={errors}
                    errorMessage='Please enter your postcode'
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='pointer-events-none w-full sm:pb-5 z-20'>
            <div className='pointer-events-auto flex items-center justify-between gap-x-6 px-6 py-2.5 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-3.5 rounded-[8px] bg-white shadow-[0.8px_0.8px_3px_0px_#B8E0DB,-0.8px_-0.8px_3px_0px_#B8E0DB]'>
              <p className="text-sm/6 font-['DM_Sans'] text-[14px] font-medium leading-normal text-[#6E7785]">
                *There are some mandatory fields. Please check and provide the
                information to continue.
              </p>
              <div className='flex justify-center items-center gap-4'>
                <button
                  type='submit'
                  className={`p-2 px-[14px] rounded-md font-['DM_Sans'] text-base font-medium leading-5 tracking-[0.4px] ${
                    profileUpdated
                      ? 'bg-[#020251] shadow-[0_1px_4px_0_rgba(0,0,0,0.25)] text-white hover:text-[#65C2D7] cursor-pointer'
                      : 'bg-[#F4F4F4] shadow-[0_1px_4px_0_rgba(0,0,0,0.25)] text-[#A0ABBE] cursor-not-allowed'
                  }`}
                  disabled={!profileUpdated}
                >
                  Save changes
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Profile
